import React from 'react';
import ProofBasis, { ProofMark } from '../components/ProofBasis';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  SparklesIcon,
  BoltIcon,
  RocketLaunchIcon,
  ChatBubbleBottomCenterTextIcon,
  CodeBracketIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { ScrollAnimation } from '../components/ScrollAnimation';
import SEO from '../components/SEO';
import { AFTER_HOURS, CRITICAL_TARGET, ORGANISATIONS } from '../data/proofPoints';
import RelatedProjects from '../components/RelatedProjects';
import { PageHeroTitle } from '../styles/typography';

// Animations
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const PageWrapper = styled.div`
  padding: var(--spacing-section-mobile) 0;
  background: linear-gradient(180deg, 
    var(--color-primary-light) 0%, 
    var(--color-primary-base) 50%,
    var(--color-primary-light) 100%);
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: var(--spacing-section) 0;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 20% 80%, rgba(11,152,206,0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(49,110,168,0.04) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: var(--spacing-4xl);
  position: relative;
  z-index: 2;
`;

const Title = PageHeroTitle;

const Subtitle = styled.p`
  font-size: var(--fs-xl);
  color: var(--text-secondary);
  max-width: 800px;
  margin: 0 auto var(--spacing-xl);
  line-height: var(--lh-relaxed);
`;

const ServicesBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-md);
  background: rgba(11,152,206,0.1);
  border: 1px solid rgba(11,152,206,0.2);
  border-radius: 999px;
  color: var(--color-accent-primary);
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--spacing-xl);
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-2xl);
  position: relative;
  z-index: 2;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ServiceCard = styled(Link)`
  display: block;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: var(--spacing-2xl);
  border: 1px solid rgba(11,152,206,0.1);
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, 
      var(--color-accent-primary), 
      var(--color-accent-secondary),
      var(--color-accent-primary));
    background-size: 200% 200%;
    animation: ${gradientMove} 8s ease infinite;
    border-radius: 24px;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  &:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: 0 25px 80px rgba(11,152,206,0.2);
    
    &::before {
      opacity: 0.3;
    }
    
    .service-icon {
      animation: ${float} 2s ease-in-out infinite;
      transform: scale(1.1);
    }
    
    .learn-more {
      svg {
        transform: translateX(6px);
      }
    }
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--color-accent-primary-fill), var(--color-accent-secondary));
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-lg);
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(11,152,206,0.3);
  
  svg {
    width: 40px;
    height: 40px;
    color: white;
  }
`;

const ServiceTitle = styled.h3`
  font-size: var(--fs-2xl);
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  font-weight: var(--font-weight-bold);
`;

const ServiceDescription = styled.p`
  color: var(--text-secondary);
  line-height: var(--lh-relaxed);
  margin-bottom: var(--spacing-lg);
  font-size: var(--fs-md);
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 var(--spacing-xl) 0;
  
  li {
    display: flex;
    align-items: center;
    margin-bottom: var(--spacing-sm);
    color: var(--text-secondary);
    font-size: var(--fs-sm);
    
    &:before {
      content: '✓';
      color: var(--color-accent-primary);
      font-weight: var(--font-weight-bold);
      font-size: var(--fs-md);
      margin-right: var(--spacing-sm);
      width: 16px;
      display: flex;
      justify-content: center;
    }
  }
`;

const LearnMoreButton = styled.div`
  color: var(--color-accent-primary);
  font-weight: var(--font-weight-bold);
  font-size: var(--fs-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.3s ease;
  }
`;

const StatsSection = styled.section`
  text-align: center;
  margin-top: var(--spacing-4xl);
  padding: var(--spacing-3xl) var(--spacing-xl);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(11,152,206,0.1);
  box-shadow: 0 20px 60px rgba(0,0,0,0.08);
  position: relative;
  z-index: 2;

  @media (max-width: 767px) {
    padding: var(--spacing-xl) var(--spacing-md);

    h2 {
      font-size: clamp(1.75rem, 1.4rem + 1.5vw, 2.25rem);
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-xl);
  margin-top: var(--spacing-xl);

  @media (max-width: 767px) {
    gap: var(--spacing-sm);
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatItem = styled.div`
  .value {
    font-size: var(--fs-3xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-accent-primary);
    display: block;
    line-height: 1;
    margin-bottom: var(--spacing-xs);
  }

  .label {
    font-size: var(--fs-sm);
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: var(--ls-wider);
  }

  @media (max-width: 767px) {
    .value {
      font-size: clamp(1.5rem, 1.3rem + 0.5vw, 1.8rem);
    }
  }
`;

const services = [
  {
    id: 'ai-solutions',
    title: 'AI Agents & Automation',
    description: 'Enhance employee capabilities with custom-built AI agents and automation. This human-centric approach transforms daily workflows, automating routine processes so your team can focus on the strategic work that drives business growth.',
    features: [
      'Enhance operational efficiency with intelligent process automation',
      'Transform raw data into clear, actionable insights',
      'Automate repetitive work while amplifying human creativity',
      'Benefit from solutions that have been validated in our own daily operations'
    ],
    icon: <SparklesIcon />,
    path: '/services/ai-solutions'
  },
  {
    id: 'it-projects',
    title: 'Strategic IT Projects',
    description: 'Achieve your technology goals with high-impact IT projects run from a standard plan: intake checklist, phased tasks, testing and handoff. Essential initiatives like system migrations and infrastructure upgrades run to a defined process, all managed with professional oversight.',
    features: [
      'Modernize core infrastructure with minimal business disruption',
      'Execute complex system integrations and data migrations',
      'Build custom software solutions to meet your specific business needs',
      'Fixed-scope plans with phased estimates and a documented handoff'
    ],
    icon: <RocketLaunchIcon />,
    path: '/services/it-projects'
  },
  {
    id: 'managed-it',
    title: 'Intelligent Managed IT',
    description: 'Keep your systems running smoothly and securely with a managed IT service that provides proactive 24/7 monitoring, after-hours on-call, and strategic guidance. This AI-augmented approach allows your team to focus on your business, not on your technology.',
    features: [
      'Prevent downtime with proactive AI monitoring',
      'Strategic cloud and server management',
      'Protect your business with advanced cybersecurity',
      'Validated in our own operations—we manage our systems with the same AI'
    ],
    icon: <BoltIcon />,
    path: '/services/managed-it'
  },
  {
    id: 'helpdesk',
    title: 'AI-Augmented Helpdesk Support',
    description: 'Provide your team with intelligent IT support designed to keep your team productive. This smarter, more proactive helpdesk experience combines our expert technicians with AI that triages and categorises requests and briefs the technician, while a person answers.',
    features: [
      'Offer expert support via phone, email, chat, or ticket',
      'Priority-based response targets, with Critical issues at 30 minutes',
      'Utilize AI to monitor systems and prevent disruptions',
      'A commitment to a responsive and efficient support experience'
    ],
    icon: <ChatBubbleBottomCenterTextIcon />,
    path: '/services/helpdesk'
  },
  {
    id: 'custom-software',
    title: 'Custom Software Development',
    description: 'Design, build and maintain line-of-business web applications, system integrations and internal portals scoped per phase in plain language, backed by automated test suites.',
    features: [
      'Line-of-business web applications and client portals',
      'Two-way API and ERP system integrations',
      'Legacy codebase rescue and modernization',
      'You own all source code, schemas and runbooks'
    ],
    icon: <CodeBracketIcon />,
    path: '/services/custom-software-development'
  }
];

const Services: React.FC = () => {
  return (
    <PageWrapper>
      <SEO title="Davinci Services | AI Solutions & Managed IT" description="Explore Davinci's AI solutions, IT project delivery, managed IT services & helpdesk support for Alberta businesses. Calgary-based since 2016." path="/services" />
      <div className="container">
        <HeroSection>
          <ScrollAnimation animation="fadeIn" delay={100}>
            <ServicesBadge>
              <SparklesIcon />
              AI That Actually Works
            </ServicesBadge>
            <Title>Technology That Amplifies Your Team</Title>
            <Subtitle>
              Our philosophy isn't to replace people; it's to amplify their capabilities. Every solution we offer
              is first validated in our own operations, ensuring proven reliability and performance.
            </Subtitle>
          </ScrollAnimation>
        </HeroSection>

        <ServicesGrid>
          {services.map((service, index) => (
            <ScrollAnimation key={service.id} animation="fadeIn" delay={200 + index * 100}>
              <ServiceCard to={service.path}>
                <ServiceIcon className="service-icon">
                  {service.icon}
                </ServiceIcon>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
                <ServiceFeatures>
                  {service.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ServiceFeatures>
                <LearnMoreButton className="learn-more">
                  Learn More
                  <ArrowRightIcon />
                </LearnMoreButton>
              </ServiceCard>
            </ScrollAnimation>
          ))}
        </ServicesGrid>

        <ScrollAnimation animation="scaleIn" delay={100}>
          <StatsSection>
            <h2>Why Alberta Teams Improve Their Workflows with AI</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '0' }}>
              Data-backed proof of our performance. These key metrics highlight our commitment to client success and system reliability.
            </p>
            <StatsGrid>
              <StatItem>
                <span className="value">{ORGANISATIONS.value}<ProofMark /></span>
                <span className="label">{ORGANISATIONS.label}</span>
              </StatItem>
              <StatItem>
                <span className="value">2016</span>
                <span className="label">Calgary-Based Since</span>
              </StatItem>
              <StatItem>
                <span className="value">{CRITICAL_TARGET.value}</span>
                <span className="label">{CRITICAL_TARGET.label}</span>
              </StatItem>
              <StatItem>
                <span className="value">{AFTER_HOURS.value}</span>
                <span className="label">{AFTER_HOURS.label}</span>
              </StatItem>
            </StatsGrid>
            <ProofBasis />
          </StatsSection>
        </ScrollAnimation>

        <ScrollAnimation animation="fadeIn" delay={100}>
          <RelatedProjects
            heading="Recent work across these services"
            intro="Every service line on this page has completed engagements behind it. The full catalogue is filterable by service, industry and technology."
          />
        </ScrollAnimation>
      </div>
    </PageWrapper>
  );
};

export default Services;